import type React from "react";
import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Box, Paper, Button, Typography, Checkbox } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import ClientInformationSection from "./ClientInformationSection";
import IntroductionLetterSection from "./IntroductionLetter";
import ServicesSection from "./ServicesSection";
import AdditionalNotesSection from "./AdditionalNotesSection";
import TermsAndConditionsSection from "./TermsAndCondition";
import PackageSelector from "./PackageSelector";
import CoverPageSelector from "./CoverPageSelector";
import type { CoverPageTemplate } from "../types/cover.types";
import type {
  Proposal,
  Client,
  ProposalService,
  Package,
  BackendPackage,
  BookingLink,
} from "../types/proposal.types";
import type { Booking } from "../types/booking.types";
import type { PackageService } from "../types/booking.types";
import apiService from "../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/common/ShowToast";
import ProposalPreview from "../proposal-preview/ProposalPreview";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/Index";
import PreviousBookingLinksSection from "./PreviousBookingLinksSection";
import AlternatePackagesSection from "./AlternatePackageSelection";
import { IntroductionLetterTemplate } from "../../configurations/introduction-letter-templates/types/introduction-letter";
import { toast } from "react-toastify";
import TestimonialsSelector from "./TestimonialsSelector";

type ServiceLike = {
  id: number;
  name?: string;
  description?: string | null;
  price: string | number;
  service_category?: string;
  service_category_display?: string;
};

const mapServiceLikeToProposalService = (
  service: ServiceLike,
): ProposalService => ({
  service: {
    id: service.id,
    name: service.name || service.description || "Service",
    description: service.description || "",
    price: Number(service.price),
    service_category: service.service_category || "",
    service_category_display:
      service.service_category_display || service.service_category || "",
    customizable: false,
    features: [],
  },
  quantity: 1,
  customizations: undefined,
});

const transformPackageServicesToProposalServices = (
  services: PackageService[],
): ProposalService[] =>
  services.map((service) =>
    mapServiceLikeToProposalService({
      id: service.id,
      description: service.description,
      price: service.price,
    }),
  );

const ProposalBuilde: React.FC = () => {
  const location = useLocation();
  const preloadedProposal: Proposal | undefined = location.state?.proposal;

  const [savedProposal, setSavedProposal] = useState<Proposal | null>(
    preloadedProposal ?? null,
  );

  const reduxTheme = useSelector((state: RootState) => state.theme);
  const isEditMode = !!savedProposal?.id;

  const [selectedClient, setSelectedClient] = useState<Client | null>(
    preloadedProposal?.client ?? null,
  );
  const [bookingId, setBookingId] = useState<number | null>(null);

  const [deceasedName, setDeceasedName] = useState(
    preloadedProposal?.deceasedName || "",
  );
  const [eventTitle, setEventTitle] = useState(
    preloadedProposal?.eventTitle || "",
  );
  const [deceasedBirthDate, setDeceasedBirthDate] = useState(
    preloadedProposal?.deceasedBirthDate || "",
  );
  const [deceasedPassedDate, setDeceasedPassedDate] = useState(
    preloadedProposal?.deceasedPassedDate || "",
  );

  const [alternatePackages, setAlternatePackages] = useState<Package[]>(
    preloadedProposal?.alternatePackages ?? [],
  );

  const [defaultIntroApplied, setDefaultIntroApplied] = useState(false);
  const [showPreviousBookingLinks, setShowPreviousBookingLinks] =
    useState(false);

  const [selectedTestimonials, setSelectedTestimonials] = useState<number[]>(
  preloadedProposal?.testimonial_ids || []
);

  const quoteState = location.state as
    | {
        fromQuoteRequest?: boolean;
        quoteId?: number;
        clientId?: number;
        deceased?: {
          first_name: string;
          surname: string;
          date_of_birth?: string;
          date_of_death?: string;
        } | null;
        event?: {
          datetime: string;
          venue_name: string;
        };
      }
    | undefined;

  const isQuoteProposal = Boolean(quoteState?.fromQuoteRequest);

  useEffect(() => {
    const fetchClientFromQuote = async () => {
      if (!quoteState?.fromQuoteRequest || !quoteState.clientId) return;

      try {
        const response = await apiService.get<{
          id: number;
          first_name: string;
          last_name: string;
          email?: string | null;
          phone?: string | null;
          address_one?: string | null;
          city?: string | null;
          country?: string | null;
        }>(`/api/contacts/contact/${quoteState.clientId}/`);

        const client = response.data;

        setSelectedClient({
          id: client.id.toString(),
          name: `${client.first_name} ${client.last_name}`,
          email: client.email || "",
          phone: client.phone || "",
          address: [client.address_one, client.city, client.country]
            .filter(Boolean)
            .join(", "),
        });
      } catch (error) {
        console.error("Failed to fetch client from quote:", error);
        showErrorToast("Unable to load client details from quote request.");
      }
    };

    fetchClientFromQuote();
  }, [quoteState]);

  const [selectedPackage, setSelectedPackage] = useState<Package | null>(
    preloadedProposal?.selectedPackage ?? null,
  );
  const [selectedServices, setSelectedServices] = useState<ProposalService[]>(
    preloadedProposal?.services ?? [],
  );

  const [introduction_letter, setIntroductionLetter] = useState(
    preloadedProposal?.introduction_letter || "",
  );

  const [additional_notes, setAdditionalNotes] = useState(
    preloadedProposal?.additional_notes || "",
  );
  const [terms_and_conditions, setTermsConditions] = useState(
    preloadedProposal?.terms_and_conditions || "",
  );

  const [preSelectedBooking, setPreSelectedBooking] = useState<Booking | null>(
    null,
  );

  useEffect(() => {
    if (isQuoteProposal) {
      setBookingType("FUNERAL");
    }
    if (quoteState?.deceased) {
      const d = quoteState.deceased;
      setDeceasedName(`${d.first_name} ${d.surname}`);
      setDeceasedBirthDate(d.date_of_birth || "");
      setDeceasedPassedDate(d.date_of_death || "");
    }
  }, [isQuoteProposal, quoteState?.deceased]);

  const [bookingType, setBookingType] = useState<
    "FUNERAL" | "EVENT" | "CONFERENCE"
  >("FUNERAL");

  const [selectedTemplate, setSelectedTemplate] =
    useState<CoverPageTemplate | null>(null);
  const [templateId] = useState<number | null>(
    preloadedProposal?.template ?? null,
  );
  const [previousBookingLinks, setPreviousBookingLinks] = useState<
    BookingLink[]
  >(preloadedProposal?.previous_booking_links || []);
  const [showAdditionalServices, setShowAdditionalServices] = useState(
    preloadedProposal?.show_additional_services !== false,
  );
  const [serviceVenue, setServiceVenue] = useState("");
  const [serviceDate, setServiceDate] = useState("");
  const [packages, setPackages] = useState<Package[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (!preloadedProposal) return;
    if (preloadedProposal.booking_id)
      setBookingId(preloadedProposal.booking_id);

    if (preloadedProposal.previous_booking_links) {
      setPreviousBookingLinks(preloadedProposal.previous_booking_links);
    }

    if (preloadedProposal.show_additional_services !== undefined) {
      setShowAdditionalServices(preloadedProposal.show_additional_services);
    }

    if (preloadedProposal.testimonial_ids) {
  setSelectedTestimonials(preloadedProposal.testimonial_ids);
}

    // Client
    if (preloadedProposal.booking?.contact) {
      const contact = preloadedProposal.booking.contact;
      setSelectedClient({
        id: contact.id.toString(),
        name: `${contact.first_name} ${contact.last_name}`,
        email: contact.email || "",
        phone: contact.phone || "",
        address: [contact.address_one, contact.city, contact.country]
          .filter(Boolean)
          .join(", "),
      });
    }

    // Detect booking type
    if (
      preloadedProposal.booking?.booking_type?.toUpperCase() === "CONFERENCE"
    ) {
      setBookingType("CONFERENCE");
      setEventTitle(preloadedProposal.booking.events?.[0]?.title || "");
    } else if (preloadedProposal.booking?.deceased?.[0]) {
      const deceased = preloadedProposal.booking.deceased[0];
      setDeceasedName(`${deceased.first_name} ${deceased.surname}`);
      setDeceasedBirthDate(deceased.date_of_birth || "");
      setDeceasedPassedDate(deceased.date_of_death || "");
      setBookingType("FUNERAL");
    } else if (preloadedProposal.booking?.events?.[0]) {
      setEventTitle(preloadedProposal.booking.events[0].title || "");
      setBookingType("EVENT");
    }

    if (preloadedProposal.services?.length) {
      setSelectedServices(preloadedProposal.services);
    }

    if (preloadedProposal.package) {
      setSelectedPackage({
        id: preloadedProposal.package.id,
        name: preloadedProposal.package.name,
        price: Number(preloadedProposal.package.base_price),
        contents:
          preloadedProposal.package.description?.split("\n").filter(Boolean) ||
          [],
        package_type: preloadedProposal.package.package_type,
      });
    }

    setIntroductionLetter(preloadedProposal.introduction_letter || "");
    setAdditionalNotes(preloadedProposal.additional_notes || "");
    setTermsConditions(preloadedProposal.terms_and_conditions || "");
  }, [preloadedProposal]);

  useEffect(() => {
    if (terms_and_conditions) return;

    let defaultTerms = "";
    switch (bookingType) {
      case "FUNERAL":
        defaultTerms =
          "Payment terms: 50% deposit required, balance due within 30 days. All services are subject to availability and final confirmation.";
        break;
      case "EVENT":
        defaultTerms =
          "Payment terms: 30% deposit required, balance due 14 days before the event. All services are subject to availability.";
        break;
      case "CONFERENCE":
        defaultTerms =
          "Payment terms: 40% deposit required, balance due 21 days before the conference. All services are subject to availability and confirmation.";
        break;
    }

    setTermsConditions(defaultTerms);
  }, [bookingType, terms_and_conditions]);

  useEffect(() => {
    const fetchBookingData = async () => {
      // const bookingIdFromState = location.state?.bookingId;
      const bookingIdFromState = quoteState?.fromQuoteRequest
        ? null
        : location.state?.bookingId;

      if (!bookingIdFromState) return;

      try {
        const response = await apiService.get<Booking>(
          `/api/bookings/booking/${bookingIdFromState}/`,
        );
        const booking = response.data;
        setPreSelectedBooking(booking);
        setBookingId(booking.id);

        // Client setup
        const contact = booking.contact;
        setSelectedClient({
          id: contact.id.toString(),
          name: `${contact.first_name} ${contact.last_name}`,
          email: contact.email || "",
          phone: contact.phone || "",
          address: [contact.address_one, contact.city, contact.country]
            .filter(Boolean)
            .join(", "),
        });

        // Detect booking type
        if (booking.booking_type?.toUpperCase() === "CONFERENCE") {
          setBookingType("CONFERENCE");
          setEventTitle(booking.events?.[0]?.title || "Conference Event");
        } else if (booking.deceased?.length) {
          setBookingType("FUNERAL");
          const deceased = booking.deceased[0];
          setDeceasedName(`${deceased.first_name} ${deceased.surname}`);
          setDeceasedBirthDate(deceased.date_of_birth);
          setDeceasedPassedDate(deceased.date_of_death);
        } else if (booking.events?.length) {
          setBookingType("EVENT");
          setEventTitle(booking.events[0].title || "Event");
        }

        if (booking.services?.[0]?.venue) {
          const service = booking.services[0];
          setServiceVenue(`${service.venue.name}, ${service.venue.city}`);
          setServiceDate(service.date_of_service);
        }

        if (booking.package_item) {
          const pkgItem = booking.package_item;
          setSelectedPackage({
            id: Number(pkgItem.package),
            name: pkgItem.name,
            price: Number(pkgItem.price),
            package_type: "Custom Package",
            contents: pkgItem.services.map((s) => s.description),
          });

          if (pkgItem.services?.length) {
            setSelectedServices(
              transformPackageServicesToProposalServices(pkgItem.services),
            );
          }
        }
      } catch (error) {
        console.error("Error fetching booking data:", error);
        showErrorToast("Failed to load booking data. Please try again.");
      }
    };

    fetchBookingData();
  }, [location.state, quoteState?.fromQuoteRequest]);

  useEffect(() => {
    const fetchPreviousTemplate = async () => {
      if (!templateId) return;
      try {
        const response = await apiService.get<{
          results: CoverPageTemplate[];
        }>("/api/configurations/proposal-templates/");
        const template = response.data.results.find((t) => t.id === templateId);
        if (template) setSelectedTemplate(template);
      } catch (error) {
        console.error("Error fetching template:", error);
      }
    };
    fetchPreviousTemplate();
  }, [templateId]);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const packageTypeMap: Record<string, string[]> = {
          FUNERAL: ["Funeral"],
          EVENT: ["Event Packages"],
          CONFERENCE: ["Conference"],
        };

        const types = packageTypeMap[bookingType] || [];

        const queryString =
          types.length > 0
            ? `?${types
                .map((t) => `package_type=${encodeURIComponent(t)}`)
                .join("&")}`
            : "";

        const response = await apiService.get<{ results: BackendPackage[] }>(
          `/api/packages/package/${queryString}`,
        );

        const backendPackages = response.data.results || [];

        const transformed = backendPackages.map((pkg) => ({
          id: pkg.id,
          name: pkg.name,
          price: Number(pkg.base_price),
          package_type: pkg.package_type,
          contents: Array.from(
            new Set(
              pkg.items?.flatMap((item) =>
                item.services?.map((svc) => svc.description),
              ) || [],
            ),
          ),
        }));

        setPackages(transformed);
      } catch (error) {
        console.error("Failed to fetch packages:", error);
      }
    };

    fetchPackages();
  }, [bookingType]);

  const replacePlaceholders = useCallback(
    (template: string): string => {
      let result = template;

      const replacements: Record<string, string> = {
        "{client_name}": selectedClient?.name || "",
        "{deceased_name}": deceasedName || "",
        "{event_title}": eventTitle || "",
        "{date_of_birth}": deceasedBirthDate || "",
        "{date_of_death}": deceasedPassedDate || "",
        "{service_venue}": serviceVenue || "",
        "{service_date}": serviceDate || "",
        "{current_date}": new Date().toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
      };

      Object.entries(replacements).forEach(([placeholder, value]) => {
        if (value) {
          result = result.replace(
            new RegExp(placeholder.replace(/[{}]/g, "\\$&"), "g"),
            value,
          );
        }
      });

      return result;
    },
    [
      selectedClient?.name,
      deceasedName,
      eventTitle,
      deceasedBirthDate,
      deceasedPassedDate,
      serviceVenue,
      serviceDate,
    ],
  );


  useEffect(() => {
    const applyDefaultIntroduction = async () => {
      if (preloadedProposal?.introduction_letter) return;

      if (defaultIntroApplied) return;

      if (!selectedClient) return;
      if (!deceasedName && !eventTitle) return;

      try {
        const response = await apiService.get<IntroductionLetterTemplate[]>(
          "/api/configurations/introduction-letter-templates/",
        );

        const normalizedCategory = bookingType.toLowerCase();

        const defaultTemplate = response.data.find(
          (t) =>
            t.category === normalizedCategory && t.is_default_template === true,
        );

        if (defaultTemplate) {
          const replacedTemplate = replacePlaceholders(
            defaultTemplate.body_template,
          );
          setIntroductionLetter(replacedTemplate);
          setDefaultIntroApplied(true);
        }
      } catch {
        toast.error("Failed to apply default introduction letter");
      }
    };

    applyDefaultIntroduction();
  }, [
    bookingType,
    selectedClient,
    deceasedName,
    eventTitle,
    preloadedProposal,
    defaultIntroApplied,
    introduction_letter,
    replacePlaceholders,
  ]);

  useEffect(() => {
    setDefaultIntroApplied(false);
  }, [bookingType]);

  const calculateTotals = () => {
    const servicesTotal = selectedServices.reduce(
      (sum, ps) => sum + ps.service.price * ps.quantity,
      0,
    );
    const packagePrice = selectedPackage ? selectedPackage.price : 0;
    const subtotal = packagePrice + servicesTotal;
    const tax = subtotal * 0.08;
    const total = subtotal + tax;
    return { subtotal, tax, total };
  };

  const handleSaveOrUpdate = async () => {
    if (!selectedClient) {
      showErrorToast("Please select a client first");
      return;
    }

    try {
      const { subtotal, tax, total } = calculateTotals();
      const service_ids = selectedServices.map((ps) => Number(ps.service.id));
      const alternate_package_ids = alternatePackages.map((pkg) => pkg.id);

      const proposalPayload: Partial<Proposal> = {
        clientId: selectedClient.id,
        client: selectedClient,
        package_id: selectedPackage?.id,
        service_ids,
        alternate_package_ids,
        deceasedName,
        deceasedBirthDate,
        deceasedPassedDate,
        eventTitle,
        introduction_letter,
        booking_id: bookingId || undefined,
        template_id: selectedTemplate?.id,
        additional_notes,
        terms_and_conditions,
        subtotal,
        tax,
        total,
        status: "draft",
        updatedAt: new Date().toISOString(),
        previous_booking_links: previousBookingLinks,
        show_additional_services: showAdditionalServices,
        show_previous_booking_links: showPreviousBookingLinks,
        testimonial_ids: selectedTestimonials,
      };
      

      let response;
      if (isEditMode && savedProposal?.id) {
        response = await apiService.patch<Proposal>(
          `/api/bookings/booking-proposal/${savedProposal.id}/`,
          proposalPayload,
        );
      } else {
        response = await apiService.post<Proposal>(
          "/api/bookings/booking-proposal/",
          proposalPayload,
        );
      }

      if (response.status === 200 || response.status === 201) {
        const updatedProposal: Proposal = response.data;
        setSavedProposal(updatedProposal);
        showSuccessToast(
          isEditMode
            ? "Proposal updated successfully!"
            : "Draft proposal saved successfully!",
        );
      } else {
        showErrorToast("Failed to save proposal. Please try again.");
      }
    } catch (err: unknown) {
      console.error("❌ Error saving proposal:", err);
      showErrorToast(
        `An error occurred while ${
          isEditMode ? "updating" : "saving"
        } the proposal.`,
      );
    }
  };

  const handleProposalUpdateFromPreview = (updatedProposal: Proposal) => {
    setSavedProposal(updatedProposal);
  };

  if (showPreview && selectedClient) {
    const { subtotal, tax, total } = calculateTotals();

    const proposal: Proposal = {
      ...savedProposal,
      booking: savedProposal?.booking ?? preSelectedBooking ?? undefined,
      clientId: selectedClient.id,
      client: selectedClient,
      deceasedName,
      deceasedBirthDate,
      deceasedPassedDate,
      alternatePackages,
      eventTitle,
      booking_id: bookingId ?? undefined,
      selectedPackage: selectedPackage ?? undefined,
      services: selectedServices,
      introduction_letter,
      additional_notes,
      terms_and_conditions,
      subtotal,
      tax,
      total,
      package_id: selectedPackage?.id,
      service_ids: selectedServices.map((ps) => Number(ps.service.id)),
      template_id: selectedTemplate?.id,
      updatedAt: new Date().toISOString(),
      validUntil:
        savedProposal?.validUntil ||
        new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      status: savedProposal?.status ?? "draft",
      createdAt: savedProposal?.createdAt ?? new Date().toISOString(),
      previous_booking_links: previousBookingLinks,
      show_additional_services: showAdditionalServices,
      show_previous_booking_links: showPreviousBookingLinks,
      testimonial_ids: selectedTestimonials,
    };

    return (
      <ProposalPreview
        proposal={proposal}
        onBack={() => setShowPreview(false)}
        selectedTemplate={selectedTemplate}
        onTemplateChange={setSelectedTemplate}
        onProposalUpdate={handleProposalUpdateFromPreview}
      />
    );
  }

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Box
        sx={{
          display: "flex",
          mb: 2,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {bookingType} Proposal Builder
        </Typography>
        <Box>
          <Button
            startIcon={<VisibilityIcon />}
            color="inherit"
            variant="outlined"
            onClick={() => setShowPreview(true)}
            sx={{ mr: 2, borderWidth: 2 }}
          >
            Preview
          </Button>
          <Button
            startIcon={isEditMode ? <EditIcon /> : <SaveIcon />}
            variant="contained"
            onClick={handleSaveOrUpdate}
            sx={{ bgcolor: reduxTheme.primary_colors[400] }}
          >
            {isEditMode ? "Update Draft" : "Save Draft"}
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 3 }}>
        <Box sx={{ flex: "0 0 400px" }}>
          <Paper sx={{ p: 3, mb: 3, boxShadow: 0 }}>
            <ClientInformationSection
              selectedClient={selectedClient}
              onClientSelect={setSelectedClient}
              deceasedName={deceasedName}
              onDeceasedNameChange={setDeceasedName}
              onDeceasedBirthDateChange={setDeceasedBirthDate}
              onDeceasedPassedDateChange={setDeceasedPassedDate}
              preSelectedBooking={preSelectedBooking}
              onBookingIdChange={setBookingId}
              eventTitle={eventTitle}
              onEventTitleChange={setEventTitle}
              bookingType={bookingType}
              onBookingTypeChange={setBookingType}
            />
          </Paper>

          <Paper sx={{ p: 3, mb: 3, boxShadow: 0 }}>
            <IntroductionLetterSection
              value={introduction_letter}
              onChange={setIntroductionLetter}
              bookingType={bookingType}
              proposalData={{
                clientName: selectedClient?.name,
                deceasedName: deceasedName,
                eventTitle: eventTitle,
                deceasedBirthDate: deceasedBirthDate,
                deceasedPassedDate: deceasedPassedDate,
                serviceVenue: serviceVenue,
                serviceDate: serviceDate,
              }}
            />
          </Paper>

          <Paper sx={{ p: 3, mb: 3, boxShadow: 0 }}>
            <PreviousBookingLinksSection
              links={previousBookingLinks}
              onChange={setPreviousBookingLinks}
              showLinks={showPreviousBookingLinks}
              onShowLinksChange={setShowPreviousBookingLinks}
            />
          </Paper>

          <Paper sx={{ p: 3, mb: 3, boxShadow: 0 }}>
            <AdditionalNotesSection
              value={additional_notes}
              onChange={setAdditionalNotes}
            />
          </Paper>

          <Paper sx={{ p: 3, mb: 3, boxShadow: 0 }}>
            <TermsAndConditionsSection
              value={terms_and_conditions}
              onChange={setTermsConditions}
            />
          </Paper>
          <Paper sx={{ p: 3, mb: 3, boxShadow: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Checkbox
                checked={showAdditionalServices}
                onChange={(e) => setShowAdditionalServices(e.target.checked)}
                sx={{
                  color: reduxTheme.primary_colors[400],
                  "&.Mui-checked": {
                    color: reduxTheme.primary_colors[400],
                  },
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.05)",
                  },
                }}
              />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Add List of Additional Services
              </Typography>
            </Box>
          </Paper>

          <Paper sx={{ p: 3, mb: 3, boxShadow: 0 }}>
  <TestimonialsSelector
    selectedIds={selectedTestimonials}
    onChange={setSelectedTestimonials}
  />
</Paper>

          <CoverPageSelector
            selectedTemplate={selectedTemplate}
            onTemplateSelect={setSelectedTemplate}
            bookingType={bookingType}
            selectedTemplateId={templateId}
          />
        </Box>

        <Box sx={{ flex: 1 }}>
          <PackageSelector
            packages={packages}
            selectedPackage={selectedPackage?.name || ""}
            onPackageSelect={(name) =>
              setSelectedPackage(packages.find((p) => p.name === name) || null)
            }
          />
          <ServicesSection
            selectedServices={selectedServices}
            onServicesChange={setSelectedServices}
          />
          <Paper sx={{ p: 3, mt: 3, boxShadow: 0 }}>
            <AlternatePackagesSection
              packages={packages.filter((p) => p.id !== selectedPackage?.id)}
              selected={alternatePackages}
              onChange={setAlternatePackages}
            />
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default ProposalBuilde;
